import { Button, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import React from 'react';
import { useEntityData, useRouteToTab } from '../../../../EntityContext';
import { ENTITY_ACTIONS } from '../../../../rbacConstantsService';
import { SidebarHeader } from '../SidebarHeader';
import DescriptionSection from './DescriptionSection';
import LinksSection from './LinksSection';
import SourceRefSection from './SourceRefSection';
import EmptyContentSection from './EmptyContentSection';
import { useTranslation } from '../../../../../../../i18n/useTranslation';
import { sanitizeDescription } from '../../../../../../shared/utils/sanitizationUtils';
import { useRbac } from '../../../../useRbac';

interface Properties {
    hideLinksButton?: boolean;
}

interface Props {
    properties?: Properties;
    readOnly?: boolean;
}

export const SidebarAboutSection = ({ properties, readOnly }: Props) => {
    const hideLinksButton = properties?.hideLinksButton;
    const { entityData, entityType } = useEntityData();
    const routeToTab = useRouteToTab();
    const { t } = useTranslation();
    const { canPerformEntityAction } = useRbac();
    const { DOCS, DOC_LINKS } = ENTITY_ACTIONS;

    const canEditDocs = canPerformEntityAction(entityType, DOCS);
    const canEditDocLinks = canPerformEntityAction(entityType, DOC_LINKS);

    const noPermissionTooltipDocs = !canEditDocs ? t('UNAUTHORISED_TOOLTIP') : undefined;
    const noPermissionTooltipDocLinks = !canEditDocLinks ? t('UNAUTHORISED_TOOLTIP') : undefined;
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;
    const isEditDocsDisabled = isReadOnly || !canEditDocs;
    const isEditLinksDisabled = isReadOnly || !canEditDocLinks;
    const editDocsTooltip = noPermissionTooltipDocs ?? (isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined);
    const editLinksTooltip = noPermissionTooltipDocLinks ?? (isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined);

    const originalDescription = entityData?.properties?.description;
    const editedDescription = entityData?.editableProperties?.description;
    const description = (editedDescription || originalDescription || '').replace(/&nbsp;/g, '');
    const links = entityData?.institutionalMemory?.elements || [];

    // Check if the string is empty
    const isDescEmpty = description.trim() === '';
    const hasContent = !!description || links.length > 0;

    return (
        <div className="pa-4">
            <SidebarHeader
                title={t('About')}
                actions={
                    hasContent && (
                        <Tooltip title={editDocsTooltip} overlayClassName="f-tooltip-white">
                            <Button
                                type="default"
                                onClick={() =>
                                    routeToTab({ tabName: t('Documentation'), tabParams: { editing: true } })
                                }
                                className="icon-button"
                                disabled={isEditDocsDisabled}
                            >
                                <EditOutlined />
                            </Button>
                        </Tooltip>
                    )
                }
            />
            {description && <DescriptionSection description={sanitizeDescription(description)} />}
            {(!hasContent || isDescEmpty) && (
                <EmptyContentSection
                    hasLinks={!(links.length > 0)}
                    hideLinksButton={hideLinksButton}
                    readOnly={isReadOnly}
                    editDocsDisabled={isEditDocsDisabled}
                    editDocsTooltip={editDocsTooltip}
                    editLinksDisabled={isEditLinksDisabled}
                    editLinksTooltip={editLinksTooltip}
                />
            )}
            {hasContent && (
                <LinksSection
                    hideLinksButton={hideLinksButton}
                    readOnly={isReadOnly}
                    editLinksDisabled={isEditLinksDisabled}
                    editLinksTooltip={editLinksTooltip}
                />
            )}
            <SourceRefSection />
        </div>
    );
};

export default SidebarAboutSection;
